import { Button, Divider, Flex } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RequestRecord } from "sandbox/types";

import { MethodBadge, SearchBar } from "sandbox/features/shared";

import s from "./RecentRequests.module.css";

dayjs.extend(relativeTime);

interface RecentRequestsProps {
  entries: RequestRecord[];
  onSelect: (entry: RequestRecord) => void;
}

export const RecentRequests = ({ entries, onSelect }: RecentRequestsProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const [query, setQuery] = useState("");

  const q = query.toLowerCase().trim();
  const filtered = entries
    .filter(entry => {
      if (!q) return true;
      const label = entry.endpointLabel ?? entry.path;
      return (
        label.toLowerCase().includes(q) ||
        entry.path.toLowerCase().includes(q) ||
        entry.method.toLowerCase().includes(q)
      );
    })
    .slice(0, 12);

  return (
    <aside className={s.root}>
      <header className={s.header}>
        {t("components.recentRequests.title")}
      </header>
      {entries.length === 0 ? (
        <p className={s.empty}>{t("components.recentRequests.empty")}</p>
      ) : (
        <div className={s.searchWrap}>
          <SearchBar value={query} onChange={setQuery} />
        </div>
      )}
      {entries.length > 0 && filtered.length === 0 && (
        <p className={s.empty}>{t("components.recentRequests.noResults")}</p>
      )}
      <ul className={s.list}>
        {filtered.map((entry, i) => (
          <li key={entry.id}>
            {i > 0 && <Divider className={s.divider} />}
            <Button
              type="text"
              className={s.item}
              onClick={() => onSelect(entry)}
            >
              <Flex vertical gap="middle">
                <Flex align="center" gap="small" wrap>
                  <MethodBadge method={entry.method} />
                  <span className={s.label}>
                    {entry.endpointLabel ?? entry.path}
                  </span>
                </Flex>
                <div className={s.path} title={entry.path}>
                  {entry.path}
                </div>
                <span className={s.meta}>
                  {dayjs(entry.sentAt).fromNow()} - {entry.durationMs} ms
                </span>
              </Flex>
            </Button>
          </li>
        ))}
      </ul>
    </aside>
  );
};
